import React from 'react';
import { X, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const Cart = () => {
  const { items, removeItem, updateQuantity, total, isOpen, setIsOpen, itemCount } = useCart();
  const navigate = useNavigate();

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/checkout');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50"
            data-testid="cart-backdrop"
          />

          {/* Cart Panel */}
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'tween', duration: 0.3 }}
            className="fixed top-0 right-0 h-full w-full max-w-md bg-white border-l border-neutral-200 z-50 flex flex-col"
            data-testid="cart-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-neutral-200">
              <div className="flex items-center gap-3">
                <ShoppingBag size={24} />
                <h2 className="text-xl font-bold tracking-tight">
                  Cart ({itemCount})
                </h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                data-testid="cart-close-button"
                className="p-2 hover:text-neutral-600 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <ShoppingBag size={48} className="text-neutral-300 mb-4" />
                  <p className="text-neutral-500">Your cart is empty</p>
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      navigate('/shop');
                    }}
                    data-testid="cart-continue-shopping"
                    className="mt-4 text-black uppercase tracking-wider text-sm font-semibold hover:underline"
                  >
                    Continue Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-6">
                  {items.map((item) => (
                    <div
                      key={item.product_id}
                      data-testid={`cart-item-${item.product_id}`}
                      className="flex gap-4 pb-6 border-b border-neutral-100"
                    >
                      {/* Image */}
                      <div className="w-20 h-20 bg-neutral-100 flex-shrink-0">
                        {item.image && (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>

                      {/* Details */}
                      <div className="flex-1">
                        <h3 className="font-semibold text-sm">
                          {item.name}
                        </h3>
                        <p className="text-black font-medium text-sm mt-1">
                          £{item.price.toFixed(2)}
                        </p>

                        {/* Quantity */}
                        <div className="flex items-center gap-3 mt-3">
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                            data-testid={`cart-decrease-${item.product_id}`}
                            className="p-1 border border-neutral-200 hover:border-black transition-colors"
                          >
                            <Minus size={14} />
                          </button>
                          <span className="text-sm w-8 text-center">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                            data-testid={`cart-increase-${item.product_id}`}
                            className="p-1 border border-neutral-200 hover:border-black transition-colors"
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeItem(item.product_id)}
                        data-testid={`cart-remove-${item.product_id}`}
                        className="text-neutral-400 hover:text-red-500 transition-colors"
                      >
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="p-6 border-t border-neutral-200 space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-500 uppercase tracking-wider text-sm">Subtotal</span>
                  <span className="text-2xl font-bold">£{total.toFixed(2)}</span>
                </div>
                <p className="text-neutral-400 text-xs">Shipping calculated at checkout</p>
                <button
                  onClick={handleCheckout}
                  data-testid="cart-checkout-button"
                  className="w-full bg-[#e84628] text-white py-4 font-semibold uppercase tracking-wider flex items-center justify-center gap-2 hover:bg-[#d63d20] transition-colors"
                >
                  Checkout
                  <ArrowRight size={18} />
                </button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Cart;
